import * as Scrivito from "scrivito";
import axios from "axios";
import { isBrowserEnv } from "utils/isWindowObjAvailable";
import logo from "assets/images/trox-logo.png";
import { excludedReportsTitles } from "variables";
import {
  getFullBodyData,
  getFullHeadData,
  getOrderCodeState,
} from "utils/productHelpers";
import { getHomepage } from "utils";
import type {
  CheckboxItem,
  OrderCodeOption,
  OrderCodePart,
  ProductImage,
} from "interfaces/product/product.types";
import type { VariablesOf } from "gql.tada";
import type { createProductXBIMReport } from "api/mutations";
import type {
  CreateLCAReportExecute,
  DownloadFileParams,
  DownloadItem,
  GetLCAReportExecute,
  GetProductInfoReportBlobParams,
  MultipleFilesDownloadParams,
  PollLCAResult,
  SingleFileDownloadParams,
} from "../interfaces/product/reportHelpers.types";
import type { TranslateStringOnly } from "hooks";

const getOrdecodeData = (parts: OrderCodePart[]): OrderCodeOption[] =>
  (parts ?? []).flatMap((part) => part.options ?? []);

export const encodeImageToBase64 = async (
  imageUrl: string | null | undefined,
): Promise<string | null> => {
  if (!imageUrl) {
    return null;
  }

  const response = await axios.get(imageUrl, { responseType: "blob" });
  const reader = new FileReader();
  reader.readAsDataURL(response.data);

  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      resolve(reader.result as string | null);
    };
    reader.onerror = reject;
  });
};

export const downloadLink = (blob: Blob, linkName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = linkName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getProductImageUrl = async (
  imagesList: ProductImage[] | null | undefined,
  imageType = "ProductPhoto",
) =>
  imagesList?.length &&
  Scrivito.load(() => {
    const productPhoto = imagesList.find((image) => image.typ === imageType);
    const productImagePimId = productPhoto ? productPhoto.pimId : null;
    const imageObject = Scrivito.Obj.where(
      "pimId",
      "equals",
      productImagePimId,
    ).first();

    if (productImagePimId && imageObject) {
      return Scrivito.urlFor(imageObject);
    }
  });

export const getProductInfoReportBlob = async ({
  productOutput,
  getReportCallback,
  lang,
  translate,
}: GetProductInfoReportBlobParams) => {
  const { product, productImageList, specificationText } = productOutput;
  if (!product) {
    console.error("Product data is not available");
    return;
  }

  const productImages = productImageList?.productImages as
    | ProductImage[]
    | null;

  const text = specificationText?.text;

  const productImageUrl = await getProductImageUrl(productImages);
  const reportImageUrl =
    productImages?.length &&
    (await Scrivito.load(() => {
      let reportImagePimId =
        productImages.find(
          (image: ProductImage) => image.typ !== "ProductPhoto",
        )?.pimId ?? null;
      // pimId = -1 is returned by EPF to indicate that no image is available for the product
      if (reportImagePimId === "-1") {
        const productListPage = getHomepage()?.get(
          "productListPage",
        ) as Scrivito.Obj;
        const defaultImage = productListPage?.get("defaultProductImage") as
          | Scrivito.Obj
          | undefined;
        reportImagePimId = (defaultImage?.get("pimId") as string) ?? null;
      }

      const imageObj = Scrivito.Obj.where(
        "pimId",
        "equals",
        reportImagePimId,
      ).first();

      return reportImagePimId && imageObj && Scrivito.urlFor(imageObj);
    }));

  const orderCodeParts = getOrdecodeData(
    (product.orderCode!.parts ?? []) as OrderCodePart[],
  );
  const logoImageUrl = isBrowserEnv() ? `${window.location.origin}${logo}` : "";

  const logoImageBase64 = await encodeImageToBase64(logoImageUrl);
  const productImageBase64 =
    (await encodeImageToBase64(productImageUrl as string)) || "";
  const reportImageBase64 =
    (await encodeImageToBase64(reportImageUrl as string)) || "";

  const strategyId = product.currentStrategyId ?? "";

  const strategy = product.availableStrategies!.filter(
    (s) => s?.id === strategyId,
  );

  const strategyInput = {
    name: translate.stringOnly("PRODUCT_CONFIGURATOR_REPORT_STRATEGY"),
    value: strategy[0]?.presentation,
  };

  const flattedInputs = [strategyInput];

  (product.inputs ?? []).forEach((i) =>
    i?.properties?.forEach((prop) => {
      if (!prop) return;
      flattedInputs.push({
        name: prop.presentation ?? "",
        value: `${prop.value ?? ""} ${prop.unit ?? ""}`,
      });
    }),
  );

  const flattedOutputs: { name: string; value: string }[] = [];
  (product.outputs ?? []).forEach((o) =>
    o?.properties?.forEach((prop) => {
      if (!prop) return;
      flattedOutputs.push({
        name: prop.presentation ?? "",
        value: `${prop.value ?? ""} ${prop.unit ?? ""}`,
      });
    }),
  );

  return getReportCallback({
    input: {
      languageIso2L: lang,
      viewImages: [
        {
          type: "Logo",
          image: (logoImageBase64 ?? "").split(",")[1],
        },
        {
          type: "Product",
          image: productImageBase64.split(",")[1],
        },
        {
          type: "Report",
          image: reportImageBase64.split(",")[1],
          imageHeader: translate
            .stringOnly("PRODUCT_CONFIGURATOR_REPORT_SCHEMATIC_SIDE_VIEW_LABEL")
            .toString(),
        },
      ],
      orderCode: {
        orderCodePresentation: product.orderCode?.presentation ?? "",
        orderCodeParts: orderCodeParts
          .filter((part) => part.state === 0)
          .map((part) => ({
            name: part.title,
            value: part.presentation || part.currentSignature.value,
            description: part.currentSignature.title,
          })),
      },
      inputs: {
        headerLabel: translate(
          "PRODUCT_CONFIGURATOR_REPORT_INPUT_LABEL",
        ).toString(),
        ioReportList: flattedInputs,
      },
      outputs: {
        headerLabel: translate(
          "PRODUCT_CONFIGURATOR_REPORT_RESULTS_LABEL",
        ).toString(),
        ioReportList: flattedOutputs,
      },
      ...(product.outputsAcoustic && {
        productAcousticHeaders: {
          acousticLabel: translate.stringOnly(
            "PRODUCT_CONFIGURATOR_REPORT_ACOUSTING_LABEL",
          ),
          acousticHeaders: getFullHeadData(product.outputsAcoustic),
        },
        acousticData: {
          productAcousticList: getFullBodyData(product.outputsAcoustic),
        },
      }),
      descriptionText: {
        descriptionLabel: translate(
          "PRODUCT_CONFIGURATOR_REPORT_DESCRRIPTION",
        ).toString(),
        description: text,
      },
      airflowPatternReport: null,
      notes: null,
      version: "EPF Version 2.10.3.5",
    },
  }).then(({ data }) => {
    if (data && data.createProductReport?.base64Data) {
      const atobw = Uint8Array.from(
        atob(data.createProductReport?.base64Data),
        (c) => c.charCodeAt(0),
      );

      const blob = new Blob([atobw], {
        type: "application/pdf",
      });
      return { blob, type: "pdf" };
    }
    return undefined;
  });
};

// Scrivito documents downloading
export const handleSingleScrivitoDocumentDownload = async (
  documentManual: Scrivito.Obj,
): Promise<{ blob: Blob; type: unknown }> => {
  const url = await Scrivito.load(() => documentManual.contentUrl());
  const documentType = documentManual.get("fileTypeDe");

  const response = await axios.get(url, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    type: documentType,
  };
};

export const handleOpenManual = (docUrl: string | null | undefined): void => {
  if (docUrl) {
    const link = document.createElement("a");
    link.href = docUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  }
};

export const importJSZip = async () => {
  const JSZip = await import("components/_Modals/ProjectDownloadModal/LazyZip");
  return JSZip.default;
};

// SingleProductDownloadModal text formatting
export const formatPresentation = (presentation: string | null | undefined) =>
  presentation &&
  presentation
    .replace(/\//g, "_")
    .replace(/-/g, "_")
    .replace(/³/g, "3")
    .replace(/[^a-zA-Z0-9_.]/g, "")
    .replace(/^[^a-zA-Z_]/, "_$&");

export const formatDisplayedName = (
  item: DownloadItem,
  presentation: string,
): string => {
  const formattedPresentation = formatPresentation(presentation);
  return `${formattedPresentation}_${item.title}`;
};

export const formatFileDescription = (
  item: DownloadItem,
  translate: TranslateStringOnly,
): string => {
  const { documentCategory } = item;
  const documentCategories: Record<string, string> = {
    product_info: translate("PRODUCT_CONFIGURATOR_INFORMATION").toString(),
    manuals: "Manual",
    certificates: "Certificate",
  };
  const getCategory = (docCategory: string): string | null =>
    documentCategories[docCategory] || null;

  return `${getCategory(documentCategory!) ?? translate.stringOnly(item.title)}`;
};

export const formatBytesToMB = (bytes: number): string => {
  const megabytes = bytes / (1024 * 1024);
  const roundedMB = megabytes.toFixed(1);
  return `${roundedMB} MB`;
};

export const getSelectedItems = (itemsData: CheckboxItem[]): string[] =>
  itemsData.reduce<string[]>((filteredSelectedItems, item) => {
    if (item.checked) {
      if (!excludedReportsTitles.includes(item.title)) {
        filteredSelectedItems.push(item.title);
      }
    }
    if (item.checkboxContent) {
      const checkedSublistTitles = item.checkboxContent
        .filter(
          (subItem) =>
            subItem.checked && !excludedReportsTitles.includes(subItem.title),
        )
        .map((subItem) => subItem.title);

      filteredSelectedItems.push(...checkedSublistTitles);
    }

    return filteredSelectedItems;
  }, []);

export const JOB_STATUS = {
  FINISHED: "finished",
  PENDING: "pending",
  FAILED: "failed",
} as const;

export const createLCAProductReport = async (
  executeCreateLCAProductReport: CreateLCAReportExecute,
  nodeId: string,
  languageIso2L: string,
) => {
  let res;
  try {
    res = await executeCreateLCAProductReport({
      input: {
        nodeId,
        languageIso2L,
      },
    });
  } catch (e) {
    console.error(e);
  }

  return res?.data;
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const pollLCAProductReport = async (
  executeGetLCAProductReport: GetLCAReportExecute,
  jobId: string,
): PollLCAResult => {
  let response;

  try {
    response = await executeGetLCAProductReport({ input: { jobId } });
  } catch (err) {
    console.error(
      `Polling executor failed: ${(err as { originalError: { message: string } }).originalError.message}`,
    );
  }

  const lcaPayload = response?.getLCAProductReport;
  const status = lcaPayload?.status;
  const url = lcaPayload?.url;
  const message = lcaPayload?.message;

  switch (status) {
    case JOB_STATUS.PENDING:
      await delay(1000);
      return pollLCAProductReport(executeGetLCAProductReport, jobId);

    case JOB_STATUS.FAILED:
      throw new Error(message || "LCA report generation failed");

    case JOB_STATUS.FINISHED:
    default:
      return { jobId, url, status, message };
  }
};

export const downloadLCAFile = async (
  downloadUrl: string,
): Promise<{ blob: Blob; type: string }> => {
  if (!downloadUrl) {
    throw new Error("No download URL provided for LCA file");
  }
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error("Failed to fetch LCA file");
  }
  const blob = await res.blob();
  return { blob, type: "pdf" };
};

export const handleSingleFileDownload = async ({
  item,
  productOutput,
  presentation,
  units,
  lang,
  orderCode,
  executeCreateProductXBIMReport,
  executeCreateProductReport,
  executeCreateLCAProductReport,
  executeGetLCAProductReport,
  documents,
  requestParams,
  country,
  translate,
  nodeId,
}: SingleFileDownloadParams) => {
  const blobToDownload = await downloadFile({
    title: item,
    productOutput,
    units,
    lang,
    orderCode,
    executeCreateProductXBIMReport,
    executeCreateProductReport,
    executeCreateLCAProductReport,
    executeGetLCAProductReport,
    documents,
    requestParams,
    country,
    translate,
    nodeId,
  });

  if (!blobToDownload || !blobToDownload.blob) {
    console.error("No file to download for item:", item);
    return;
  }

  downloadLink(
    blobToDownload.blob,
    `${presentation}_${item}.${blobToDownload.type}`,
  );
};

export const handleMultipleFilesDownload = async ({
  selectedItems,
  productOutput,
  presentation,
  units,
  lang,
  orderCode,
  executeCreateProductXBIMReport,
  executeCreateProductReport,
  executeCreateLCAProductReport,
  executeGetLCAProductReport,
  documents,
  requestParams,
  country,
  translate,
  nodeId,
}: MultipleFilesDownloadParams) => {
  const Zip = await importJSZip();
  const zip = new Zip();

  await Promise.all(
    selectedItems.map(async (item: string) => {
      const blobToDownload = await downloadFile({
        title: item,
        productOutput,
        units,
        lang,
        orderCode,
        executeCreateProductXBIMReport,
        executeCreateProductReport,
        executeCreateLCAProductReport,
        executeGetLCAProductReport,
        documents,
        requestParams,
        country,
        translate,
        nodeId,
      });
      if (blobToDownload?.type) {
        zip.file(
          `${presentation}_${item}.${blobToDownload.type}`,
          blobToDownload.blob,
        );
      }
    }),
  );
  const zipBlob = await zip.generateAsync({ type: "blob" });
  downloadLink(zipBlob, `${presentation}_reports.zip`);
};

export const findMatchingDocument = (
  title: string,
  scrivitoDocuments: Scrivito.Obj[],
): Scrivito.Obj | null => {
  for (const document of scrivitoDocuments) {
    const documentTitle = document.get("title");

    if (documentTitle === title) {
      return document;
    }
  }
  return null;
};

export const downloadFile = async ({
  title,
  productOutput,
  units,
  lang,
  orderCode,
  country,
  executeCreateProductXBIMReport,
  executeCreateProductReport,
  executeCreateLCAProductReport,
  executeGetLCAProductReport,
  documents,
  requestParams,
  translate,
  nodeId,
}: DownloadFileParams) => {
  switch (title) {
    case "SPECIFICATION_TEXT":
      // place to add it in the Future once epf will be ready to provide it.
      return null;
    case "BIM_DATA": {
      if (!country) {
        console.error("Missing country code for XBIM report");
        return;
      }

      const { data } = await executeCreateProductXBIMReport({
        input: {
          systemOfUnits: units,
          languageIso2L: lang,
          countryIso2L: country,
          ...(nodeId
            ? { nodeId }
            : {
                seriesId: orderCode?.seriesId ?? requestParams?.seriesId,
                state:
                  requestParams?.state ??
                  ({
                    orderCodeState: {
                      optionStates: getOrderCodeState(
                        productOutput?.product?.orderCode?.parts,
                      ),
                    },
                    currentStrategyId:
                      productOutput?.product?.currentStrategyId,
                    selectedAccessories:
                      productOutput?.product?.selectedAccessories,
                  } as VariablesOf<
                    typeof createProductXBIMReport
                  >["input"]["state"]),
              }),
        },
      });

      if (data?.createProductXBIMReport?.base64Data) {
        const atobw = Uint8Array.from(
          atob(data.createProductXBIMReport?.base64Data),
          (c) => c.charCodeAt(0),
        );
        const blob = new Blob([atobw], {
          type: "application/xml",
        });

        return {
          blob,
          type: "xml",
        };
      } else if (data?.createProductXBIMReport !== null) {
        console.error("Invalid data");
      }
      return;
    }
    case "PRODUCT_INFORMATION_REPORT":
      if (!productOutput) return undefined;
      return getProductInfoReportBlob({
        productOutput,
        getReportCallback: executeCreateProductReport,
        lang,
        translate,
        nodeId,
      });
    case "LCA REPORT":
      try {
        const lcaData = await createLCAProductReport(
          executeCreateLCAProductReport,
          nodeId,
          lang,
        );
        if (!lcaData) return undefined;
        const result = lcaData.createLCAProductReport;

        const { jobId, status, message, url } = result ?? {};

        if (status === JOB_STATUS.FINISHED && url) {
          return downloadLCAFile(url);
        }

        if (status === JOB_STATUS.FAILED) {
          console.error(message || "LCA report generation failed");
        }

        if (status === JOB_STATUS.PENDING && jobId) {
          const finished = await pollLCAProductReport(
            executeGetLCAProductReport,
            jobId,
          );

          if (finished?.url) {
            return downloadLCAFile(finished.url);
          }

          console.error("No URL returned after LCA finished");
        }

        if (url) {
          return downloadLCAFile(url);
        }

        console.error("Unexpected response creating LCA report");
      } catch (err) {
        console.error("LCA report download error:", err);
        throw err;
      }
      break;
    default:
      if (title) {
        const matchingProdInfo = findMatchingDocument(
          title,
          documents?.productInfo || [],
        );
        const matchingCertificate = findMatchingDocument(
          title,
          documents?.certificates || [],
        );
        const matchingManual = findMatchingDocument(
          title,
          documents?.manuals || [],
        );
        const matchingDocument =
          matchingProdInfo || matchingCertificate || matchingManual;

        if (matchingDocument) {
          return handleSingleScrivitoDocumentDownload(matchingDocument);
        }
      }
      console.error("unknown file type");
  }
};

export const addScrivitoDocumentToSublist = async (
  scrivitoDocuments: Scrivito.Obj[],
) => {
  if (!scrivitoDocuments.length) {
    return [];
  }

  return Promise.all(
    scrivitoDocuments.map(async (document) => {
      const contentLength = await Scrivito.load(() => document.contentLength());
      const contentUrl = await Scrivito.load(() => document.contentUrl());
      const scrivitoId = await Scrivito.load(() => document.id());

      return {
        id: scrivitoId,
        title: document.get("title") as string,
        checked: false,
        extension: document.get("fileTypeDe") as string,
        documentCategory: document.get("categoryDownloadType") as string,
        contentLength,
        fromScrivito: true,
        contentUrl,
      };
    }),
  );
};
